import { reloadData } from '@axo/form/data-access/backend-sync';
import { ga, tracking } from '@axo/form/data-access/tracking';
import { Product } from '@axo/shared/data-access/types';
import { useAnalytics } from '@axo/shared/services/analytics';
import { useSubject } from '@axo/shared/util/reactivity';
import { useEffect } from 'react';
import {
  type GetAnalyticsData,
  getAnalyticsData,
} from '../../shared/analytics/getAnalyticsData';
import { Step } from '../../types';
import { updateQueryParams } from '../../utils/updateQueryParams';
import { storeUTM } from './utils/storeUTM';

const setUrlStep = (stepNumber: number) => {
  updateQueryParams('step', stepNumber.toString());
};

interface Props {
  product?: Product;
  data: object;
  baseUrl: string;
  steps?: Step[];
  stepNumber?: number;
}

export const useTracking = ({
  product,
  steps,
  stepNumber,
  data,
  baseUrl,
}: Props) => {
  const { track } = useAnalytics();
  const { completedSteps } = useSubject(tracking.state);

  useEffect(() => {
    /**
     * @todo investigate: Steps & stepNumber are never being passed as arguments to useTracking
     */
    if (steps && stepNumber) {
      ga.sendGAPageLoadEvent({
        name: steps[stepNumber - 1].name,
        number: stepNumber,
      });
      // For GA tracking
      setUrlStep(stepNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepNumber]);

  async function onViewed(args: GetAnalyticsData) {
    await track({
      ...getAnalyticsData({
        ...args,
        product,
        action: 'Viewed',
      }),
    });
  }

  async function onInitiated(args: GetAnalyticsData) {
    const gaPromise = ga.sendGAEvent({
      event: 'GAEvent',
      eventCategory: 'Form',
      eventAction: `Form Initiated`,
      eventLabel: '',
      page: window.location.pathname,
    });

    const analyticsPromise = track({
      ...getAnalyticsData({
        ...args,
        product,
        action: 'Initiated',
      }),
    });

    await Promise.all([gaPromise, analyticsPromise]);

    // Store UTMs in Cookies
    storeUTM();
  }

  async function completeStep(
    stepName: string,
    args: GetAnalyticsData,
    product?: Product
  ) {
    const { applicationId, customerJWT } = reloadData.get(product);
    if (!customerJWT)
      throw new Error('cannot complete step without the customer JWT');
    if (!applicationId)
      throw new Error('cannot complete step without the loan application id');

    const promise = tracking.completeStep({
      step: { name: stepName },
      data,
      baseUrl,
      token: customerJWT,
      loanApplicationId: applicationId,
    });

    const analyticsPromise = track({
      ...getAnalyticsData({
        ...args,
        product,
        action: 'Completed',
      }),
    });

    await Promise.all([promise, analyticsPromise]);
  }

  async function completeApplication(
    stepName: string,
    args: GetAnalyticsData,
    product?: Product
  ) {
    const { customerId } = args;

    const { applicationId, customerJWT } = reloadData.get(product);
    if (!customerJWT)
      throw new Error('cannot track completion without the customer JWT');
    if (!applicationId)
      throw new Error(
        'cannot track completion without the loan application id'
      );
    if (!customerId)
      throw new Error('cannot track completion without the customer id');

    const applicationPromise = tracking.completeApplication({
      step: { name: stepName },
      data,
      baseUrl,
      token: customerJWT,
      loanApplicationId: applicationId,
    });

    const analyticsPromise = track({
      ...getAnalyticsData({
        ...args,
        product,
        action: 'Completed',
      }),
    });

    await Promise.all([applicationPromise, analyticsPromise]);
  }

  return {
    completedSteps,
    onViewed,
    onInitiated,
    completeStep,
    completeApplication,
  };
};
